// ══════════════════════════════════════════════════════════════════════════════
//  LogoLoader: animacion de carga de marca.
// ══════════════════════════════════════════════════════════════════════════════
import { useRef, useLayoutEffect } from 'react';
import { C, LOGO_SRC } from '../../design.js';

// ══════════════════════════════════════════════════════════════════════════
//  LogoLoader — animación de carga de marca
//  Usa el logo (PNG con fondo transparente) como MÁSCARA CSS sobre un degradado
//  azul en movimiento: el glifo del logo "fluye" mientras carga. Monocromático
//  (familia primary). Reutilizable en cualquier estado de espera del proyecto.
//    <LogoLoader size={40} label="Generando…" />
// ══════════════════════════════════════════════════════════════════════════
const LOGO_LOADER_ANIM_ID = '__gb_logo_loader_anim';
const ensureLogoAnim = () => {
  if (typeof document === 'undefined') return;
  let s = document.getElementById(LOGO_LOADER_ANIM_ID);
  if (!s) { s = document.createElement('style'); s.id = LOGO_LOADER_ANIM_ID; document.head.appendChild(s); }
  // Banda de luz que CRUZA físicamente el logo vía transform (animación confiable,
  // sin las rarezas de background-position con backgrounds sobredimensionados).
  // Excepción a prefers-reduced-motion: un loader es movimiento ESENCIAL (comunica
  // actividad). La regla por clase (más específica) vence al reset global con `*`.
  s.textContent =
    '@keyframes gbLogoSweep{0%{transform:translateX(-110%)}100%{transform:translateX(280%)}}' +
    '@media (prefers-reduced-motion:reduce){.gb-logo-band{animation-duration:var(--gb-logo-dur,1.4s)!important;animation-iteration-count:infinite!important}}';
};
ensureLogoAnim();
const LOGO_ASPECT = 527 / 266;  // proporción real del LOGO_SRC

// La escala de la marca (canon 2026-09-22, lienzo "Esperar en calma"): un
// tamaño por el espacio que espera, siempre al centro de ese espacio.
//   ventana 56: toda la ventana (el arranque)
//   area    44: el área de trabajo, una hoja (Espera)
//   caja    32: una caja: la tarjeta, la vista previa, el aviso que flota
//   linea   18: en una línea: la barra, la miniatura
export const MARCA = Object.freeze({ ventana: 56, area: 44, caja: 32, linea: 18 });
const LogoLoader = ({ size = 40, label, speed = 1.4, style: sx = {} }) => {
  const bandRef = useRef(null);
  useLayoutEffect(() => {
    ensureLogoAnim();  // garantiza el keyframe al montar (robusto ante HMR)
    // Inline !important vence SIEMPRE al reset global de reduced-motion
    // (`* {animation-duration:0.01ms!important}`), sin depender de especificidad.
    // Un loader es movimiento esencial (comunica actividad), por eso lo forzamos.
    if (bandRef.current) bandRef.current.style.setProperty('animation', `gbLogoSweep ${speed}s linear infinite`, 'important');
  }, [speed]);
  const w = Math.round(size * LOGO_ASPECT);
  return (
    <div role="status" aria-label={label || 'Leyendo'} data-gb-cargando=""
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: label ? 12 : 0, ...sx }}>
      <div style={{
        position: 'relative', width: w, height: size, overflow: 'hidden',
        background: C.primaryFill,  // logo en azul institucional sólido
        WebkitMaskImage: `url(${LOGO_SRC})`, maskImage: `url(${LOGO_SRC})`,
        WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center', maskPosition: 'center',
        WebkitMaskSize: 'contain', maskSize: 'contain',
      }}>
        {/* Banda de luz que barre el logo (recortada por la máscara del glifo). */}
        <div ref={bandRef} className="gb-logo-band" style={{
          position: 'absolute', top: 0, bottom: 0, left: 0, width: '55%',
          background: `linear-gradient(100deg, transparent 0%, ${C.primaryMid} 35%, ${C.primaryLight} 50%, ${C.primaryMid} 65%, transparent 100%)`,
          '--gb-logo-dur': `${speed}s`,
          animation: `gbLogoSweep ${speed}s linear infinite`,
          willChange: 'transform',
        }} />
      </div>
      {label && <div style={{ fontSize: 13, color: C.textMuted, fontWeight: 500 }}>{label}</div>}
    </div>
  );
};


export { LogoLoader };
